import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import { ArrowLeft, Save, Eye, X, Plus, Image as ImageIcon } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Toaster, toast } from "sonner";
import MediaPicker from "@/admin/components/MediaPicker";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const EMPTY = {
  slug: "",
  company: "",
  industry: "",
  headline: "",
  excerpt: "",
  image: "",
  metric: "",
  metric_label: "",
  published: true,
  subtitle: "",
  hero_image: "",
  facts: [],
  quote_text: "",
  quote_author: "",
  quote_role: "",
  quote_avatar: "",
  challenge: [],
  solution: [],
  results: [],
};

function slugify(s) {
  return (s || "")
    .toLowerCase()
    .replace(/['"]/g, "")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 80);
}

export default function AdminCaseStudyEditPage() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const isNew = !slug || slug === "new";

  const [form, setForm] = useState(EMPTY);
  const [loading, setLoading] = useState(!isNew);
  const [saving, setSaving] = useState(false);
  const [mediaTarget, setMediaTarget] = useState(null); // "image" | "hero_image" | "quote_avatar"

  useEffect(() => {
    if (isNew) return;
    (async () => {
      try {
        const { data } = await axios.get(`${API}/case-studies/${slug}`);
        setForm({
          ...EMPTY,
          ...data,
          tags: data.tags || [],
          facts: data.facts || [],
          challenge: data.challenge || [],
          solution: data.solution || [],
          results: data.results || [],
          image: data.image || "",
          hero_image: data.hero_image || "",
          quote_avatar: data.quote_avatar || "",
        });
      } catch {
        toast.error("Case study not found.");
        navigate("/admin/case-studies");
      } finally {
        setLoading(false);
      }
    })();
  }, [slug, isNew, navigate]);

  const update = (patch) => setForm((prev) => ({ ...prev, ...patch }));

  const updateListItem = (key, index, value) => {
    setForm((prev) => {
      const next = [...(prev[key] || [])];
      next[index] = value;
      return { ...prev, [key]: next };
    });
  };

  const addListItem = (key, blank = "") =>
    setForm((prev) => ({ ...prev, [key]: [...(prev[key] || []), blank] }));

  const removeListItem = (key, index) =>
    setForm((prev) => ({
      ...prev,
      [key]: (prev[key] || []).filter((_, i) => i !== index),
    }));

  const handleSave = async () => {
    if (!form.company.trim() || !form.headline.trim()) {
      toast.error("Company and headline are required.");
      return;
    }
    if (isNew && !form.slug.trim()) {
      toast.error("Slug is required.");
      return;
    }
    setSaving(true);
    try {
      if (isNew) {
        const payload = { ...form, slug: form.slug || slugify(form.company) };
        await axios.post(`${API}/admin/case-studies`, payload);
        toast.success("Case study created.");
        navigate(`/admin/case-studies/${payload.slug}`);
      } else {
        const { slug: _, ...patch } = form;
        await axios.patch(`${API}/admin/case-studies/${slug}`, patch);
        toast.success("Saved.");
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Save failed.");
    } finally {
      setSaving(false);
    }
  };

  const handleMediaPicked = (url) => {
    if (mediaTarget) update({ [mediaTarget]: url });
    setMediaTarget(null);
  };

  if (loading) {
    return (
      <div className="p-10">
        <p className="text-sm text-slate-400">Loading…</p>
      </div>
    );
  }

  return (
    <div data-testid="admin-cs-edit-page" className="bg-slate-50 min-h-full">
      <Toaster position="top-right" richColors />

      <div className="sticky top-0 z-30 bg-white border-b border-slate-200 px-5 lg:px-8 py-3 flex items-center justify-between gap-3">
        <div className="flex items-center gap-3 min-w-0">
          <Link to="/admin/case-studies" className="text-slate-400 hover:text-slate-700 p-1.5 rounded hover:bg-slate-100" title="Back">
            <ArrowLeft className="h-4 w-4" />
          </Link>
          <div className="min-w-0">
            <p className="text-xs text-slate-400 font-mono truncate">
              {isNew ? "/admin/case-studies/new" : `/case-studies/${form.slug}`}
            </p>
            <p className="text-sm font-bold text-slate-900 truncate">{form.company || "New case study"}</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {!isNew && (
            <a
              href={`/case-studies/${form.slug}`}
              target="_blank"
              rel="noopener noreferrer"
              className="text-xs text-slate-500 hover:text-sky-600 inline-flex items-center gap-1"
            >
              <Eye className="h-3 w-3" /> Preview
            </a>
          )}
          <Button
            onClick={handleSave}
            disabled={saving}
            data-testid="admin-cs-save"
            className="bg-sky-600 hover:bg-sky-700 text-white h-9 rounded-lg text-xs font-semibold"
          >
            <Save className="h-3.5 w-3.5 mr-1" /> {saving ? "Saving…" : "Save"}
          </Button>
        </div>
      </div>

      <div className="p-5 lg:p-8">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-5 lg:gap-7 max-w-6xl mx-auto">
          <div className="lg:col-span-2 space-y-5">
            <SectionCard title="Card / Listing">
              <Field label="Company">
                <Input
                  value={form.company}
                  onChange={(e) => update({ company: e.target.value })}
                  placeholder="PromoWorks Inc."
                  data-testid="admin-cs-company"
                />
              </Field>
              {isNew && (
                <Field label="Slug" hint="Permanent after save.">
                  <Input
                    value={form.slug}
                    onChange={(e) => update({ slug: slugify(e.target.value) })}
                    placeholder="promoworks"
                    className="font-mono text-xs"
                  />
                </Field>
              )}
              <Field label="Industry">
                <Input
                  value={form.industry}
                  onChange={(e) => update({ industry: e.target.value })}
                  placeholder="Promotional Products"
                />
              </Field>
              <Field label="Headline">
                <Input
                  value={form.headline}
                  onChange={(e) => update({ headline: e.target.value })}
                  placeholder="How PromoWorks reduced processing time by 40%"
                />
              </Field>
              <Field label="Excerpt">
                <Textarea
                  rows={2}
                  value={form.excerpt}
                  onChange={(e) => update({ excerpt: e.target.value })}
                  placeholder="One-line summary used on listing cards."
                />
              </Field>
              <div className="grid grid-cols-2 gap-3">
                <Field label="Metric" hint="e.g. +40%">
                  <Input value={form.metric} onChange={(e) => update({ metric: e.target.value })} />
                </Field>
                <Field label="Metric label" hint="e.g. Faster Processing">
                  <Input value={form.metric_label} onChange={(e) => update({ metric_label: e.target.value })} />
                </Field>
              </div>
              <ImagePickerField
                label="Card image"
                value={form.image}
                onChange={(v) => update({ image: v })}
                onPick={() => setMediaTarget("image")}
              />
            </SectionCard>

            <SectionCard title="Story Detail">
              <Field label="Subtitle">
                <Textarea
                  rows={2}
                  value={form.subtitle}
                  onChange={(e) => update({ subtitle: e.target.value })}
                  placeholder="One-paragraph intro shown under the headline on the detail page."
                />
              </Field>
              <ImagePickerField
                label="Hero image"
                value={form.hero_image}
                onChange={(v) => update({ hero_image: v })}
                onPick={() => setMediaTarget("hero_image")}
              />

              <ListEditor
                label="Facts (Industry, Team Size, …)"
                items={form.facts}
                placeholderA="Label"
                placeholderB="Value"
                renderItem={(item, i) => (
                  <div className="grid grid-cols-2 gap-2 flex-1">
                    <Input
                      value={item.label || ""}
                      onChange={(e) => updateListItem("facts", i, { ...item, label: e.target.value })}
                      placeholder="Industry"
                      className="h-9 text-xs"
                    />
                    <Input
                      value={item.value || ""}
                      onChange={(e) => updateListItem("facts", i, { ...item, value: e.target.value })}
                      placeholder="Promotional Products"
                      className="h-9 text-xs"
                    />
                  </div>
                )}
                onAdd={() => addListItem("facts", { label: "", value: "" })}
                onRemove={(i) => removeListItem("facts", i)}
              />

              <ListEditor
                label="Challenge bullets"
                items={form.challenge}
                renderItem={(item, i) => (
                  <Input
                    value={item}
                    onChange={(e) => updateListItem("challenge", i, e.target.value)}
                    placeholder="What went wrong before…"
                    className="h-9 text-xs flex-1"
                  />
                )}
                onAdd={() => addListItem("challenge", "")}
                onRemove={(i) => removeListItem("challenge", i)}
              />

              <ListEditor
                label="Solution bullets"
                items={form.solution}
                renderItem={(item, i) => (
                  <Input
                    value={item}
                    onChange={(e) => updateListItem("solution", i, e.target.value)}
                    placeholder="What Antera changed…"
                    className="h-9 text-xs flex-1"
                  />
                )}
                onAdd={() => addListItem("solution", "")}
                onRemove={(i) => removeListItem("solution", i)}
              />

              <ListEditor
                label="Results (metric + label)"
                items={form.results}
                renderItem={(item, i) => (
                  <div className="grid grid-cols-2 gap-2 flex-1">
                    <Input
                      value={item.metric || ""}
                      onChange={(e) => updateListItem("results", i, { ...item, metric: e.target.value })}
                      placeholder="+40%"
                      className="h-9 text-xs"
                    />
                    <Input
                      value={item.label || ""}
                      onChange={(e) => updateListItem("results", i, { ...item, label: e.target.value })}
                      placeholder="Faster order processing"
                      className="h-9 text-xs"
                    />
                  </div>
                )}
                onAdd={() => addListItem("results", { metric: "", label: "" })}
                onRemove={(i) => removeListItem("results", i)}
              />
            </SectionCard>

            <SectionCard title="Customer Quote">
              <Field label="Quote text">
                <Textarea
                  rows={3}
                  value={form.quote_text}
                  onChange={(e) => update({ quote_text: e.target.value })}
                  placeholder={'\u201cAntera transformed how we\u2026\u201d'}
                />
              </Field>
              <div className="grid grid-cols-2 gap-3">
                <Field label="Quote author">
                  <Input
                    value={form.quote_author}
                    onChange={(e) => update({ quote_author: e.target.value })}
                    placeholder="Sarah Mitchell"
                  />
                </Field>
                <Field label="Author role">
                  <Input
                    value={form.quote_role}
                    onChange={(e) => update({ quote_role: e.target.value })}
                    placeholder="Operations Director, PromoWorks Inc."
                  />
                </Field>
              </div>
              <ImagePickerField
                label="Author avatar (headshot)"
                value={form.quote_avatar}
                onChange={(v) => update({ quote_avatar: v })}
                onPick={() => setMediaTarget("quote_avatar")}
              />
            </SectionCard>
          </div>

          <aside className="lg:col-span-1 space-y-5">
            <div className="bg-white border border-slate-200 rounded-xl p-4">
              <p className="text-[10px] font-bold text-slate-500 uppercase tracking-widest mb-3">Publishing</p>
              <label className="flex items-center justify-between gap-3 text-sm">
                <span className="text-slate-700">Published</span>
                <input
                  type="checkbox"
                  checked={form.published}
                  onChange={(e) => update({ published: e.target.checked })}
                  data-testid="admin-cs-published"
                  className="w-4 h-4 accent-sky-600"
                />
              </label>
              <p className="text-[10px] text-slate-400 mt-2">
                Drafts show as &ldquo;Coming soon&rdquo; on /case-studies.
              </p>
            </div>
          </aside>
        </div>
      </div>

      <MediaPicker
        open={!!mediaTarget}
        onClose={() => setMediaTarget(null)}
        onPick={handleMediaPicked}
        mode="picker"
      />
    </div>
  );
}

function SectionCard({ title, children }) {
  return (
    <div className="bg-white border border-slate-200 rounded-xl p-5 space-y-4">
      <h3 className="text-xs font-bold text-slate-900 uppercase tracking-widest">{title}</h3>
      {children}
    </div>
  );
}

function Field({ label, hint, children }) {
  return (
    <div className="space-y-1.5">
      <Label className="text-[10px] font-bold text-slate-500 uppercase tracking-widest">{label}</Label>
      {children}
      {hint && <p className="text-[10px] text-slate-400">{hint}</p>}
    </div>
  );
}

function ListEditor({ label, items, renderItem, onAdd, onRemove }) {
  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between">
        <Label className="text-[10px] font-bold text-slate-500 uppercase tracking-widest">{label}</Label>
        <button
          type="button"
          onClick={onAdd}
          className="text-[10px] font-semibold text-sky-600 hover:text-sky-700 inline-flex items-center gap-1"
        >
          <Plus className="h-3 w-3" /> Add
        </button>
      </div>
      <div className="space-y-1.5">
        {items.length === 0 ? (
          <p className="text-[10px] text-slate-400 italic">No items yet.</p>
        ) : (
          items.map((item, i) => (
            <div key={i} className="flex items-center gap-2">
              {renderItem(item, i)}
              <button
                type="button"
                onClick={() => onRemove(i)}
                className="text-slate-400 hover:text-red-500 p-1 shrink-0"
                aria-label="Remove"
              >
                <X className="h-3.5 w-3.5" />
              </button>
            </div>
          ))
        )}
      </div>
    </div>
  );
}

function ImagePickerField({ label, value, onChange, onPick }) {
  return (
    <Field label={label}>
      {value && (
        <div className="rounded-lg overflow-hidden border border-slate-200 relative">
          <img src={value} alt="" className="w-full h-28 object-cover" />
          <button
            type="button"
            onClick={() => onChange("")}
            className="absolute top-2 right-2 bg-white/90 text-slate-600 hover:text-red-500 rounded-full p-1 shadow"
            aria-label="Remove"
          >
            <X className="h-3 w-3" />
          </button>
        </div>
      )}
      <div className="flex items-center gap-2">
        <Button
          type="button"
          onClick={onPick}
          variant="outline"
          className="h-9 text-xs rounded-lg shrink-0"
        >
          <ImageIcon className="h-3.5 w-3.5 mr-1" />
          {value ? "Change" : "Pick from library"}
        </Button>
        <Input
          value={value || ""}
          onChange={(e) => onChange(e.target.value)}
          placeholder="…or paste URL"
          className="text-xs font-mono"
        />
      </div>
    </Field>
  );
}
